import { Component, DestroyRef, ElementRef, OnInit, inject, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { finalize } from 'rxjs/operators';
import { CurrentContextService } from '../../../core/context/current-context.service';
import { LocalizationService } from '../../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../../shared/design-system/page.component';
import { AinStateComponent } from '../../../shared/design-system/state.component';
import {
  SEARCH_OPS_REDIRECT_TYPES,
  SearchPropertyListItemDto,
  SearchRedirectDto,
  SearchRedirectImportResultDto
} from './growth-search-ops.models';
import { GrowthSearchOpsService } from './growth-search-ops.service';

@Component({
  standalone: true,
  selector: 'ain-growth-search-ops-redirects',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    AinFormFieldComponent,
    FormsModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    SelectModule,
    TableModule,
    RouterLink
  ],
  template: `
    <ain-page
      [eyebrow]="t('growth.eyebrow')"
      [title]="t('growth.searchops.redirects.title')"
      [description]="t('growth.searchops.redirects.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.searchops.redirects.help.title'"
        [descriptionKey]="'growth.searchops.redirects.help.description'"
        [bulletKeys]="[
          'growth.searchops.redirects.help.bullets.loop',
          'growth.searchops.redirects.help.bullets.dryRun',
          'growth.searchops.redirects.help.bullets.apply'
        ]" />

      <div class="toolbar">
        <button pButton type="button" severity="secondary" [label]="t('common.refresh')" (click)="load(true)"></button>
        <a pButton severity="secondary" [routerLink]="['/growth/search-ops/urls']" [queryParams]="propertyQuery()" [label]="t('growth.searchops.workHome.actions.urls')"></a>
        <a pButton severity="secondary" [routerLink]="['/growth/search-ops/apply-queue']" [queryParams]="propertyQuery()" [label]="t('growth.searchops.workHome.actions.applyQueue')"></a>
      </div>

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('common.error')" [message]="error()" />
      } @else if (properties().length === 0) {
        <ain-state type="empty" [title]="t('growth.searchops.workHome.empty.title')" [message]="t('growth.searchops.workHome.empty.message')" />
      } @else {
        <section class="card">
          <ain-form-field [label]="t('growth.searchops.fields.property')">
            <p-select
              [options]="propertyOptions()"
              optionLabel="label"
              optionValue="value"
              [(ngModel)]="selectedPropertyId"
              appendTo="body"
              (onChange)="onPropertyChange($event.value)" />
          </ain-form-field>
        </section>

        <section class="card">
          <div class="section-title">
            <h3>{{ t('growth.searchops.redirects.sections.map') }}</h3>
            <ain-info-hint
              [titleKey]="'growth.searchops.redirects.mapHelp.title'"
              [descriptionKey]="'growth.searchops.redirects.mapHelp.description'" />
          </div>
          <div class="toolbar">
            <button pButton type="button" [label]="t('growth.searchops.redirects.actions.add')" (click)="dialogOpen = true"></button>
            @if (canApply()) {
              <button pButton type="button" severity="secondary" [label]="t('growth.searchops.redirects.actions.importCsv')" (click)="openCsvPicker()"></button>
              <input #csvFileInput type="file" accept=".csv,text/csv" hidden (change)="onCsvSelected($event)" />
            }
          </div>
          @if (importSummary()) {
            <p class="summary">{{ importSummary() }}</p>
          }
          @if (importErrors().length > 0) {
            <ul class="errors">
              @for (row of importErrors(); track row.fromPath + row.toUrl) {
                <li>{{ row.fromPath }} → {{ row.toUrl }}: {{ t('growth.searchops.errors.' + (row.error || 'unknown')) }}</li>
              }
            </ul>
          }
          <p-table [value]="redirects()" [paginator]="true" [rows]="20" dataKey="id">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('growth.searchops.fields.fromPath') }}</th>
                <th>{{ t('growth.searchops.fields.toUrl') }}</th>
                <th>{{ t('growth.searchops.fields.redirectType') }}</th>
                <th>{{ t('growth.searchops.fields.status') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td class="wrap-url">{{ row.fromPath }}</td>
                <td class="wrap-url">{{ row.toUrl }}</td>
                <td>{{ row.redirectType }}</td>
                <td>{{ t('growth.searchops.redirectStatus.' + row.status) }}</td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="4">{{ t('growth.searchops.redirects.empty') }}</td>
              </tr>
            </ng-template>
          </p-table>
        </section>
      }
    </ain-page>

    <p-dialog
      [header]="t('growth.searchops.redirects.dialogs.add')"
      [(visible)]="dialogOpen"
      [modal]="true"
      [style]="{ width: '32rem' }"
      appendTo="body">
      <ain-info-hint
        [titleKey]="'growth.searchops.redirects.dialogHelp.title'"
        [descriptionKey]="'growth.searchops.redirects.dialogHelp.description'" />
      <ain-form-field [label]="t('growth.searchops.fields.fromPath')">
        <input pInputText [(ngModel)]="model.fromPath" />
      </ain-form-field>
      <ain-form-field [label]="t('growth.searchops.fields.toUrl')">
        <input pInputText [(ngModel)]="model.toUrl" />
      </ain-form-field>
      <ain-form-field [label]="t('growth.searchops.fields.redirectType')">
        <p-select [options]="typeOptions()" optionLabel="label" optionValue="value" [(ngModel)]="model.redirectType" appendTo="body" />
      </ain-form-field>
      <ain-form-field [label]="t('growth.searchops.fields.reason')">
        <input pInputText [(ngModel)]="model.reason" />
      </ain-form-field>
      <div class="toolbar">
        <button pButton type="button" [disabled]="busy()" [label]="t('common.save')" (click)="save()"></button>
      </div>
    </p-dialog>
  `,
  styles: [`
    .toolbar { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; margin-bottom: 12px; }
    .card { background: var(--ain-color-surface, #fff); padding: 16px; border-radius: 12px; margin-bottom: 12px; }
    .section-title { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
    .section-title h3 { margin: 0; font-size: 1rem; }
    .wrap-url { overflow-wrap: anywhere; }
    .summary { color: #64748b; }
    .errors { color: #9a3412; overflow-wrap: anywhere; }
  `]
})
export class GrowthSearchOpsRedirectsComponent implements OnInit {
  private readonly api = inject(GrowthSearchOpsService);
  private readonly localization = inject(LocalizationService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly context = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  readonly csvFileInput = viewChild<ElementRef<HTMLInputElement>>('csvFileInput');

  readonly loading = signal(false);
  readonly busy = signal(false);
  readonly error = signal('');
  readonly properties = signal<SearchPropertyListItemDto[]>([]);
  readonly redirects = signal<SearchRedirectDto[]>([]);
  readonly importSummary = signal<string | null>(null);
  readonly importErrors = signal<SearchRedirectImportResultDto['rows']>([]);
  selectedPropertyId: string | null = null;
  dialogOpen = false;
  model = { fromPath: '', toUrl: '', redirectType: '301', reason: '' };

  ngOnInit(): void {
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      this.selectedPropertyId = params.get('propertyId');
      this.load(true);
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  canApply(): boolean {
    const ctx = this.context.context();
    if (ctx?.roles?.includes('SystemAdmin') || ctx?.isPlatformAdmin) {
      return true;
    }
    return (ctx?.permissions ?? []).some(item => item.key === 'growth.searchops.apply');
  }

  propertyOptions() {
    return this.properties().map(item => ({ value: item.id, label: `${item.name} (${item.primaryDomain})` }));
  }

  typeOptions() {
    return SEARCH_OPS_REDIRECT_TYPES.map(value => ({ value, label: value }));
  }

  propertyQuery() {
    return this.selectedPropertyId ? { propertyId: this.selectedPropertyId } : {};
  }

  onPropertyChange(propertyId: string): void {
    void this.router.navigate([], { relativeTo: this.route, queryParams: { propertyId }, queryParamsHandling: 'merge' });
  }

  load(showSpinner: boolean): void {
    if (showSpinner) {
      this.loading.set(true);
    }
    this.error.set('');
    this.api.listProperties().pipe(finalize(() => this.loading.set(false))).subscribe({
      next: properties => {
        this.properties.set(properties);
        if (!this.selectedPropertyId && properties.length > 0) {
          this.selectedPropertyId = properties[0].id;
        }
        this.loadBoard();
      },
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.loadFailed'))
    });
  }

  save(): void {
    if (!this.selectedPropertyId) {
      return;
    }
    this.busy.set(true);
    this.api.upsertRedirect({
      searchPropertyId: this.selectedPropertyId,
      fromPath: this.model.fromPath,
      toUrl: this.model.toUrl,
      redirectType: this.model.redirectType,
      reason: this.model.reason || null
    }).pipe(finalize(() => this.busy.set(false))).subscribe({
      next: () => {
        this.dialogOpen = false;
        this.loadBoard();
      },
      error: err => this.error.set(this.errorKey(err?.error?.error))
    });
  }

  openCsvPicker(): void {
    this.csvFileInput()?.nativeElement.click();
  }

  onCsvSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file || !this.selectedPropertyId) {
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const csv = String(reader.result ?? '');
      this.busy.set(true);
      this.api.importRedirects(this.selectedPropertyId!, csv, true).subscribe({
        next: dryRun => {
          this.importErrors.set(dryRun.rows.filter(item => !!item.error));
          this.importSummary.set(this.t('growth.searchops.redirects.dryRunResult')
            .replace('{accepted}', String(dryRun.accepted))
            .replace('{rejected}', String(dryRun.rejected)));
          this.api.importRedirects(this.selectedPropertyId!, csv, false)
            .pipe(finalize(() => this.busy.set(false)))
            .subscribe({
              next: () => this.loadBoard(),
              error: err => this.error.set(this.errorKey(err?.error?.error))
            });
        },
        error: err => {
          this.busy.set(false);
          this.error.set(this.errorKey(err?.error?.error));
        }
      });
    };
    reader.readAsText(file);
  }

  private errorKey(code: string | undefined): string {
    if (code === 'growth_searchops_redirect_loop' || code === 'growth_searchops_redirect_chain') {
      return this.t('growth.searchops.errors.' + code.replace('growth_searchops_', ''));
    }
    return code ?? this.t('growth.searchops.messages.saveFailed');
  }

  private loadBoard(): void {
    if (!this.selectedPropertyId) {
      this.redirects.set([]);
      return;
    }
    this.api.listRedirects(this.selectedPropertyId).subscribe({
      next: items => this.redirects.set(items),
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.loadFailed'))
    });
  }
}
